import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  useToast,
} from "@chakra-ui/react";
import AccountApi from "api/account";

const getFilenameFromDisposition = (contentDisposition) => {
  if (!contentDisposition) return null;

  // RFC 5987: filename*=UTF-8''...
  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1].replace(/(^"|"$)/g, ""));
    } catch (e) {
      return utf8Match[1].replace(/(^"|"$)/g, "");
    }
  }

  const plainMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
  if (plainMatch && plainMatch[1]) {
    return plainMatch[1].trim().replace(/(^"|"$)/g, "");
  }

  return null;
};

const AccountExcelModal = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [region, setRegion] = useState("us");
  const [isChild, setIsChild] = useState(false);
  const [parentEmail, setParentEmail] = useState("");

  const parentEmailRequired = useMemo(() => Boolean(isChild), [isChild]);

  const resetForm = () => {
    setRegion("us");
    setIsChild(false);
    setParentEmail("");
  };

  const handleClose = () => {
    onClose?.();
    resetForm();
  };

  const validate = () => {
    if (!region) return "ریجن الزامی است.";
    if (parentEmailRequired && !String(parentEmail || "").trim()) return "ایمیل والد الزامی است.";
    return null;
  };

  const handleSubmit = async () => {
    const errorMessage = validate();
    if (errorMessage) {
      toast({
        title: "خطا",
        description: errorMessage,
        status: "error",
        duration: 2500,
        position: "top-right",
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        region,
        is_child: Boolean(isChild),
        parent_email: parentEmailRequired ? String(parentEmail || "").trim() : null,
      };

      const response = await AccountApi.GenerateExcel(payload);

      const filename =
        getFilenameFromDisposition(response?.headers?.["content-disposition"]) || "accounts.xlsx";
      const blobType =
        response?.headers?.["content-type"] ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([response.data], { type: blobType });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "موفقیت",
        description: "فایل اکسل ساخته شد.",
        status: "success",
        duration: 2000,
        position: "top-right",
        isClosable: true,
      });

      handleClose();
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        e?.message ||
        "دریافت فایل اکسل ناموفق بود.";
      toast({
        title: "خطا",
        description: message,
        status: "error",
        duration: 3000,
        position: "top-right",
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>ساخت اکسل اکانت‌ها</ModalHeader>
        <ModalCloseButton position="absolute" left="9px" right="auto" />
        <ModalBody>
          <FormControl mb={4} isRequired>
            <FormLabel>ریجن</FormLabel>
            <Select textAlign="left" value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="us">US</option>
              <option value="tr">TR</option>
            </Select>
          </FormControl>

          <FormControl mb={4}>
            <Checkbox isChecked={isChild} onChange={(e) => setIsChild(e.target.checked)}>
              اکانت کودک؟
            </Checkbox>
          </FormControl>

          <FormControl mb={2} isRequired={parentEmailRequired} isDisabled={!parentEmailRequired}>
            <FormLabel>ایمیل والد</FormLabel>
            <Input
              textAlign="left"
              type="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              placeholder="parent@example.com"
            />
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>
            ساخت فایل
          </Button>
          <Button variant="ghost" onClick={handleClose} ml={3}>
            بستن
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AccountExcelModal;

